import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from '../../utils/axiosConfig';
import {
    Ticket,
    DollarSign,
    TrendingUp,
    CreditCard,
    Copy,
    ExternalLink,
    CheckCircle2,
    AlertCircle,
    Smartphone,
    ChevronRight,
    ArrowUpRight,
    Coins,
    Clock
} from 'lucide-react';

const RrppDashboard = () => {
    const { slug } = useParams();
    const { user } = useSelector(state => state.auth);
    const [events, setEvents] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(null);
    const [bankDetails, setBankDetails] = useState({
        cbu: '',
        alias: '',
        bankName: '',
        titular: ''
    });
    const [savingBank, setSavingBank] = useState(false);
    const [bankMessage, setBankMessage] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [evResp, commResp] = await Promise.all([
                    axios.get(`/${slug}/api/events`),
                    axios.get(`/${slug}/api/rrpp/my-commissions`)
                ]);
                setEvents(evResp.data);
                setData(commResp.data);
                if (commResp.data.summary.paymentDetails) {
                    setBankDetails(prev => ({ ...prev, ...commResp.data.summary.paymentDetails }));
                }
            } catch (err) {
                console.error('Error fetching RRPP dashboard data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [slug]);

    const copyLink = (eventId) => {
        const link = `${window.location.origin}/${slug}/events/${eventId}?ref=${user.id}`;
        navigator.clipboard.writeText(link);
        setCopied(eventId);
        setTimeout(() => setCopied(null), 2000);
    };

    const handleSaveBank = async (e) => {
        e.preventDefault();
        setSavingBank(true);
        setBankMessage('');
        try {
            await axios.put(`/${slug}/api/rrpp/payment-details`, { paymentDetails: bankDetails });
            setBankMessage({ text: 'Datos guardados correctamente.', type: 'success' });
            setTimeout(() => setBankMessage(''), 3000);
        } catch (err) {
            setBankMessage({ text: 'Error al guardar datos.', type: 'error' });
        } finally {
            setSavingBank(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#080808] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#080808] text-white selection:bg-primary-orange/30 pb-20">
            {/* HERO / SUMMARY */}
            <div className="relative pt-12 pb-24 px-6 border-b border-white/5 bg-white/[0.01]">
                <div className="mx-auto max-w-7xl">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full mb-4">
                                <TrendingUp size={12} className="text-primary-orange" />
                                <span className="text-[10px] font-black uppercase tracking-[2px] text-white/60">Tu Panel de Ventas</span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-2">Hola, <span className="text-gradient leading-relaxed">{user.name.split(' ')[0]}</span></h1>
                            <p className="text-white/40 font-medium tracking-tight">Gestioná tus links de referido y seguí tus ganancias al instante.</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <StatCard
                            label="Tickets Vendidos"
                            value={data?.summary?.totalSales || 0}
                            icon={<Ticket size={24} />}
                            color="orange"
                        />
                        <StatCard
                            label="Total Ganado"
                            value={`$${data?.summary?.totalEarned?.toLocaleString() || 0}`}
                            icon={<Coins size={24} />}
                            color="green"
                            trend="Comisión 5%"
                        />
                        <StatCard
                            label="Pendiente de Cobro"
                            value={`$${data?.summary?.unpaid?.toLocaleString() || 0}`}
                            icon={<DollarSign size={24} />}
                            color="red"
                            highlight={data?.summary?.unpaid > 0}
                        />
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-6 -mt-12 relative z-10 space-y-12">

                {/* DATOS DE COBRO */}
                <section>
                    <div className="bg-glass p-8 rounded-[40px] border border-white/10 shadow-3xl">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 bg-blue-500/10 rounded-2xl border border-blue-500/20 text-blue-400">
                                <CreditCard size={24} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black tracking-tight">Datos de Cobro</h2>
                                <p className="text-sm text-white/40 font-medium">Completá tus datos para recibir tus comisiones por transferencia.</p>
                            </div>
                        </div>

                        <form onSubmit={handleSaveBank} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <InputField
                                label="CBU / CVU"
                                value={bankDetails.cbu}
                                onChange={(val) => setBankDetails({ ...bankDetails, cbu: val })}
                                placeholder="00000031000..."
                            />
                            <InputField
                                label="Alias"
                                value={bankDetails.alias}
                                onChange={(val) => setBankDetails({ ...bankDetails, alias: val })}
                                placeholder="mi.alias.pago"
                            />
                            <InputField
                                label="Banco / Billetera"
                                value={bankDetails.bankName}
                                onChange={(val) => setBankDetails({ ...bankDetails, bankName: val })}
                                placeholder="Mercado Pago, etc."
                            />
                            <InputField
                                label="Titular de la cuenta"
                                value={bankDetails.titular}
                                onChange={(val) => setBankDetails({ ...bankDetails, titular: val })}
                                placeholder="Nombre Completo"
                            />

                            <div className="md:col-span-2 lg:col-span-4 flex items-center justify-between pt-4 border-t border-white/5 mt-2">
                                <div className="text-sm">
                                    {bankMessage && (
                                        <div className={`flex items-center gap-2 font-bold ${bankMessage.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                                            {bankMessage.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                                            {bankMessage.text}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    disabled={savingBank}
                                    className="px-10 py-4 bg-white text-black rounded-2xl font-black hover:scale-105 active:scale-95 transition-all shadow-xl disabled:bg-white/20 disabled:text-white/40"
                                >
                                    {savingBank ? 'GUARDANDO...' : 'GUARDAR DATOS'}
                                </button>
                            </div>
                        </form>
                    </div>
                </section>

                {/* LINKS DE VENTA */}
                <section>
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-2 bg-white/5 rounded-xl border border-white/10">
                            <Smartphone size={20} className="text-primary-orange" />
                        </div>
                        <h2 className="text-xl font-black tracking-tight uppercase tracking-[2px]">Mis Links de <span className="text-primary-orange">Venta</span></h2>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        {events.length === 0 ? (
                            <div className="bg-glass p-12 rounded-[32px] text-center border-dashed">
                                <p className="text-white/20 font-bold uppercase tracking-widest text-xs">No hay eventos activos para vender</p>
                            </div>
                        ) : (
                            events.map(event => (
                                <div key={event._id} className="bg-glass p-6 rounded-[32px] group hover:border-white/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-6">
                                    <div className="flex items-center gap-5">
                                        <div className="w-16 h-16 rounded-2xl overflow-hidden border border-white/10 flex-shrink-0">
                                            {event.coverImage ? (
                                                <img src={event.coverImage} alt={event.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                            ) : (
                                                <div className="w-full h-full bg-white/5 flex items-center justify-center text-white/20"><Ticket size={24} /></div>
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black mb-1 group-hover:text-primary-orange transition-colors">{event.title}</h3>
                                            <p className="text-sm text-white/30 font-bold flex items-center gap-2">
                                                {new Date(event.date).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                <span className="w-1 h-1 rounded-full bg-white/20" />
                                                {event.city || event.location}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => copyLink(event._id)}
                                            className={`flex-1 md:flex-none px-6 py-3 rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all ${copied === event._id
                                                ? 'bg-green-500 text-white translate-y-[-2px]'
                                                : 'bg-white/5 text-white/60 hover:bg-primary-orange hover:text-white border border-white/5'
                                                }`}
                                        >
                                            {copied === event._id ? (
                                                <><CheckCircle2 size={18} /> ¡COPIADO!</>
                                            ) : (
                                                <><Copy size={18} /> COPIAR LINK</>
                                            )}
                                        </button>
                                        <Link
                                            to={`/${slug}/events/${event._id}`}
                                            className="px-6 py-3 bg-white/5 border border-white/5 rounded-2xl text-white/40 hover:text-white hover:bg-white/10 transition-all font-black text-sm flex items-center gap-2 group/link"
                                        >
                                            VER <ExternalLink size={16} className="group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform" />
                                        </Link>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </section>

                {/* DETALLE DE VENTAS */}
                <section>
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-2 bg-white/5 rounded-xl border border-white/10">
                            <TrendingUp size={20} className="text-green-500" />
                        </div>
                        <h2 className="text-xl font-black tracking-tight uppercase tracking-[2px]">Historial de <span className="text-green-500">Ventas</span></h2>
                    </div>

                    <div className="bg-glass rounded-[40px] overflow-hidden border border-white/5">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="bg-white/5 text-[10px] font-black uppercase tracking-[3px] text-white/40">
                                        <th className="px-8 py-5">Evento</th>
                                        <th className="px-8 py-5">Fecha Venta</th>
                                        <th className="px-8 py-5">Comisión</th>
                                        <th className="px-8 py-5 text-right">Estado</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {data?.tickets?.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-8 py-16 text-center">
                                                <p className="text-sm font-bold text-white/10 uppercase tracking-[4px]">Sin actividad de ventas aún</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        data?.tickets?.map(t => (
                                            <tr key={t._id} className="group hover:bg-white/[0.02] transition-colors">
                                                <td className="px-8 py-6 font-black text-lg">{t.event.title}</td>
                                                <td className="px-8 py-6 text-sm text-white/40 font-medium">
                                                    {new Date(t.purchaseDate).toLocaleDateString()}
                                                </td>
                                                <td className="px-8 py-6">
                                                    <p className="text-lg font-black text-green-400">+${t.commissionAmount.toLocaleString()}</p>
                                                </td>
                                                <td className="px-8 py-6 text-right">
                                                    <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${t.commissionStatus === 'paid'
                                                        ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                                        : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                                                        }`}>
                                                        {t.commissionStatus === 'paid' ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                                                        {t.commissionStatus === 'paid' ? 'Pagado' : 'Pendiente'}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

const StatCard = ({ label, value, icon, color, trend, highlight }) => {
    const colors = {
        orange: 'bg-primary-orange/10 text-primary-orange border-primary-orange/20',
        green: 'bg-green-500/10 text-green-400 border-green-500/20',
        red: 'bg-red-500/10 text-red-400 border-red-500/20',
    };
    return (
        <div className={`bg-glass p-8 rounded-[40px] border border-white/5 group hover:border-white/20 transition-all shadow-xl relative overflow-hidden ${highlight ? 'ring-2 ring-primary-orange/30' : ''}`}>
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border mb-6 ${colors[color]} group-hover:scale-110 transition-transform`}>
                {icon}
            </div>
            <p className="text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-2">{label}</p>
            <p className="text-4xl font-black tracking-tighter mb-4 leading-none">{value}</p>
            {trend && (
                <div className="flex items-center gap-1.5 text-[10px] font-black text-white/20 uppercase tracking-widest">
                    <ArrowUpRight size={14} className="text-green-400" /> {trend}
                </div>
            )}
        </div>
    );
};

const InputField = ({ label, value, onChange, placeholder }) => (
    <div>
        <label className="block text-[10px] font-black text-white/30 uppercase tracking-[2px] mb-2 px-2">{label}</label>
        <input
            type="text"
            className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 font-bold text-white placeholder:text-white/10 focus:ring-4 focus:ring-primary-orange/10 focus:border-primary-orange/30 outline-none transition-all"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
        />
    </div>
);

export default RrppDashboard;
